import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';

type View = 'history' | 'request';
export default function OrderCardMenu({ orderSeq, hasHistory, canRequest, onSelect }: {
  orderSeq: number; hasHistory: boolean; canRequest: boolean; onSelect: (view: View) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const focusLast = useRef(false);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const items = menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
    items?.[focusLast.current ? items.length - 1 : 0]?.focus();
    const outside = (event: PointerEvent) => { if (!wrapper.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  const choose = (view: View) => { setOpen(false); trigger.current?.focus(); onSelect(view); };
  const keyboard = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault(); event.stopPropagation(); setOpen(false); trigger.current?.focus(); return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (!open) { focusLast.current = event.key === 'ArrowUp' || event.key === 'End'; setOpen(true); return; }
    const items = Array.from(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
      : (index + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length;
    items[next]?.focus();
  };
  return <MenuWrapper ref={wrapper} onKeyDown={keyboard} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }}>
    <MenuTrigger ref={trigger} type="button" aria-label={`주문 ${orderSeq} 더보기`} aria-haspopup="menu" aria-expanded={open}
      aria-controls={open ? id : undefined} onClick={() => { focusLast.current = false; setOpen(value => !value); }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg>
    </MenuTrigger>
    {open && <Menu ref={menu} id={id} role="menu" aria-label={`주문 ${orderSeq} 메뉴`}>
      {hasHistory && <button type="button" role="menuitem" tabIndex={-1} onClick={() => choose('history')}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 11a9 9 0 1 1 2.5 7M3 5v6h6M12 7v5l3 2" /></svg>처리 내역 보기
      </button>}
      {canRequest && <button type="button" role="menuitem" tabIndex={-1} className="cancel-request" onClick={() => choose('request')}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="m9 9 6 6m0-6-6 6" /></svg>취소 요청
      </button>}
    </Menu>}
  </MenuWrapper>;
}
const MenuWrapper = styled.div`position:absolute;top:6px;right:6px;z-index:2;`;
const MenuTrigger = styled.button`
  display:grid;place-items:center;width:44px;height:44px;padding:0;border:0;border-radius:6px;
  background:transparent;color:var(--user-text-muted);cursor:pointer;
  &:hover,&[aria-expanded="true"]{background:var(--user-surface-raised);color:var(--user-text);}
  &:focus-visible{outline:2px solid var(--user-accent);outline-offset:2px;}
`;
const Menu = styled.div`
  position:absolute;top:46px;right:0;min-width:176px;padding:4px;border:1px solid var(--user-border);border-radius:8px;
  background:var(--user-surface-raised);color:var(--user-text);
  button{display:flex;align-items:center;gap:10px;width:100%;min-height:44px;padding:10px 12px;border:0;border-radius:5px;background:transparent;color:inherit;font:inherit;font-size:13px;text-align:left;white-space:nowrap;cursor:pointer;}
  button:hover{background:var(--user-accent-soft);}
  button:focus-visible{outline:2px solid var(--user-accent);outline-offset:-2px;}
  button.cancel-request{color:var(--user-error);}
  svg{flex:none;}
`;
